import React from "react";
import SubPageLayout from "../components/SubPageLayout";
import { motion } from "framer-motion";
import { NAAVI_PROGRAMS } from "../data/site";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const accentColor = {
  cornflower: "#648DE5", lime: "#C6D947", teal: "#5BC4B8", turquoise: "#3AB0D6",
};

export default function ProgramsPage() {
  return (
    <SubPageLayout
      eyebrow="Programmes"
      title={<>From first steps to <span className="italic font-normal">big-school ready</span>.</>}
      subtitle="Four gently rising programmes — each with its own tempo, its own wonder. All anchored by the Cambridge Early Years approach and our unhurried way of teaching."
      accent="teal"
      decor="rainbow"
    >
      <section className="py-14 md:py-24 bg-[#FAFAF5]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-2 gap-5">
            {NAAVI_PROGRAMS.map((p, i) => (
              <motion.div
                key={p.id}
                id={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.07 }}
                className="bento-card group relative bg-[#E8F1DF] rounded-3xl overflow-hidden border border-[#0F1B47]/6 scroll-mt-32"
                data-testid={`prog-${p.id}`}
              >
                <div className="grid sm:grid-cols-5">
                  <div className="sm:col-span-2 relative aspect-[4/3] sm:aspect-auto overflow-hidden">
                    <img decoding="async" loading="lazy" src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute top-4 left-4">
                      <span className="inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-3 py-1.5 rounded-full text-[#0F1B47]" style={{ background: accentColor[p.accent] }}>
                        {p.age}
                      </span>
                    </div>
                  </div>
                  <div className="sm:col-span-3 p-7">
                    <h3 className="font-display text-2xl text-[#0F1B47] font-medium mb-1">{p.name}</h3>
                    <p className="text-[13px] font-semibold tracking-wide text-[#648DE5] mb-3">{p.tagline}</p>
                    <p className="text-[#4A5568] text-sm leading-relaxed font-light mb-5">{p.blurb}</p>
                    <Link
                      to="/admissions#enquiry"
                      className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#0F1B47] hover:underline"
                    >
                      Enquire about {p.name} <ArrowUpRight size={14} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Ratios */}
          <div className="mt-14 bg-[#0F1B47] rounded-3xl p-8 md:p-12 grid md:grid-cols-3 gap-8 text-[#FDFDF9]">
            <Stat v="1:6" l="Ratio · Toddlers" />
            <Stat v="1:10" l="Ratio · Early Years 1, 2 & 3" />
            <Stat v="8:30 — 3:30" l="Mon — Fri · Half/Full day options" />
          </div>
        </div>
      </section>
    </SubPageLayout>
  );
}

function Stat({ v, l }) {
  return (
    <div>
      <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#C6D947] mb-2">{l}</div>
      <div className="font-display text-3xl md:text-4xl font-semibold leading-none">{v}</div>
    </div>
  );
}
